import { isInternalUser } from '@steerfi/shared';
import {
  IconBuildingBank,
  IconCash,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconCurrencyDollar,
  IconFileDescription,
  IconReceipt,
  IconUsers,
  IconWallet,
} from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { type MetricsPeriod, PERIOD_OPTIONS } from '@/api/metrics';
import { EnhancedMetricCard } from '@/components/enhanced-metric-card';
import { Typography } from '@/components/ui/typography';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { useDashboardData } from '@/hooks/use-dashboard-data';
import { cn } from '@/lib/utils';

function DashboardHeader({
  titleClassName,
  subtitle,
}: {
  titleClassName?: string;
  subtitle: string;
}) {
  return (
    <div>
      <h1 className={cn('text-2xl font-bold tracking-tight pb-[0.3125rem]', titleClassName)}>
        Dashboard
      </h1>
      <Typography variant="muted">{subtitle}</Typography>
    </div>
  );
}

export const DashboardContent: React.FC = () => {
  const [period] = useState<MetricsPeriod>('last_3_months');
  const { formattedValues, isLoading, error } = useDashboardData(period);
  const { user } = useKeycloakAuth();
  const { labels } = useSupplierDisplay();
  const showInternalStats = isInternalUser(user?.roles);
  const dashboardSubtitle = `Real-time insights into your financial performance and ${labels.plural.toLowerCase()} management`;

  const toSuppliers = (params?: Record<string, string>) => {
    if (!params || Object.keys(params).length === 0) return '/suppliers';
    return `/suppliers?${new URLSearchParams(params).toString()}`;
  };

  const activePeriod = PERIOD_OPTIONS.find((p) => p.value === period);
  const periodTitle = activePeriod?.shortTitle || 'MTD';

  const periodDateRange = (() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 3);
    // Use local date parts so users in UTC− timezones don't get a boundary shifted to tomorrow UTC.
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from_date: fmt(from), to_date: fmt(to) };
  })();

  const toWithDates = (base: string) => {
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}from_date=${periodDateRange.from_date}&to_date=${periodDateRange.to_date}`;
  };

  // Show error state if data loading failed
  if (error) {
    return (
      <div className="flex flex-col gap-6 px-4 py-4 md:gap-6 md:py-6 lg:px-6">
        <div className="flex items-center justify-between">
          <DashboardHeader titleClassName="font-bold" subtitle={dashboardSubtitle} />
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Typography variant="h3" color="destructive" className="mb-2">
              Failed to Load Dashboard Data
            </Typography>
            <Typography variant="muted" className="mb-4">
              {error}
            </Typography>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Welcome Header */}
      <div className="flex-shrink-0 px-4 pt-[1.6875rem] pb-4 md:px-6 bg-transparent border-b">
        <DashboardHeader subtitle={dashboardSubtitle} />
      </div>

      {/* Key Performance Indicators */}
      <div className="flex-shrink-0 px-4 py-6 md:px-6 bg-transparent border-b">
        {/* Period selector */}
        <div className="flex items-center justify-between mb-4">
          <Typography variant="muted" className="text-sm font-medium">
            Key Metrics
          </Typography>
        </div>

        {showInternalStats ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2">
              <EnhancedMetricCard
                title="Total Clients"
                subtitle="Active client base"
                value={formattedValues?.totalClients || '0'}
                icon={IconUsers}
                isLoading={isLoading}
                to="/clients"
              />
              <EnhancedMetricCard
                title="Total Beneficiaries"
                subtitle="Active beneficiary network"
                value={formattedValues?.totalSuppliers || '0'}
                icon={IconUsers}
                isLoading={isLoading}
                to={toSuppliers()}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 @xl:grid-cols-3">
              <EnhancedMetricCard
                title={`${periodTitle} Volume`}
                subtitle={activePeriod?.label || 'Recent'}
                value={formattedValues?.periodPaymentVolume || '$0'}
                icon={IconCurrencyDollar}
                isLoading={isLoading}
                to={toWithDates('/payments')}
              />
              <EnhancedMetricCard
                title={`${periodTitle} Files`}
                subtitle={activePeriod?.label || 'Recent'}
                value={formattedValues?.periodPaymentFiles || '0'}
                icon={IconFileDescription}
                isLoading={isLoading}
                to={toWithDates('/payment-files')}
              />
              <EnhancedMetricCard
                title={`${periodTitle} Payments`}
                subtitle={activePeriod?.label || 'Recent'}
                value={formattedValues?.periodPayments || '0'}
                icon={IconCash}
                isLoading={isLoading}
                to={toWithDates('/payments')}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2 @3xl:grid-cols-4">
            {/* Row 1: Total Beneficiaries, Approved, Denied, Awaiting Approval */}
            <EnhancedMetricCard
              title={`Total ${labels.plural}`}
              subtitle={`${labels.plural} in your network`}
              value={formattedValues?.totalSuppliers || '0'}
              icon={IconUsers}
              isLoading={isLoading}
              to={toSuppliers()}
            />
            <EnhancedMetricCard
              title="Approved"
              subtitle={`${labels.plural} approved by you`}
              value={formattedValues?.beneficiaryApproved ?? '0'}
              icon={IconCircleCheck}
              isLoading={isLoading}
              to={toSuppliers({ relationshipApproval: 'APPROVED' })}
            />
            <EnhancedMetricCard
              title="Denied"
              subtitle={`${labels.plural} denied`}
              value={formattedValues?.beneficiaryDenied ?? '0'}
              icon={IconCircleX}
              isLoading={isLoading}
              to={toSuppliers({ relationshipApproval: 'DENIED' })}
            />
            <EnhancedMetricCard
              title="Awaiting Approval"
              subtitle={`${labels.plural} awaiting your review`}
              value={formattedValues?.beneficiaryAwaitingApproval ?? '0'}
              icon={IconClock}
              isLoading={isLoading}
              to={toSuppliers({ relationshipApproval: 'NULL' })}
            />
            {/* Row 2: ACH, Digital Wallet, Check */}
            <EnhancedMetricCard
              title="ACH"
              subtitle={`${labels.plural} set up for ACH payments`}
              value={formattedValues?.beneficiaryAchCount ?? '0'}
              icon={IconBuildingBank}
              isLoading={isLoading}
              to={toSuppliers({ preferredPaymentMethod: 'ACH' })}
            />
            <EnhancedMetricCard
              title="Digital Wallet"
              subtitle={`${labels.plural} set up for digital wallet payments`}
              value={formattedValues?.beneficiaryDigitalWalletCount ?? '0'}
              icon={IconWallet}
              isLoading={isLoading}
              to={toSuppliers({ preferredPaymentMethod: 'DIGITAL_WALLET' })}
            />
            <EnhancedMetricCard
              title="Check"
              subtitle={`${labels.plural} set up for check payments`}
              value={formattedValues?.beneficiaryCheckCount ?? '0'}
              icon={IconReceipt}
              isLoading={isLoading}
              to={toSuppliers({ preferredPaymentMethod: 'CHECK' })}
            />
          </div>
        )}
      </div>
    </div>
  );
};
